import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Shield, ShieldOff, Ban, RotateCcw } from "lucide-react";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = () => {
    api.get(`/admin/users?q=${encodeURIComponent(q)}`).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const action = async (u, verb, msg) => {
    try {
      if (verb === "delete") {
        // No delete endpoint — provide a stub message
        toast.info("Deletion isn't available. Use suspend instead.");
        return;
      }
      await api.post(`/admin/users/${u.user_id}/${verb}`);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="overline text-safety">Users</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">User management</h1>

      <div className="mt-6 flex gap-2 max-w-md">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search email, name, company…" data-testid="admin-users-search" className="rounded-none h-11" />
        <Button onClick={load} data-testid="admin-users-search-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11">Search</Button>
      </div>

      <div className="mt-6 border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-sage-light">
            <tr className="text-left">
              <th className="p-3 overline text-xs">User</th>
              <th className="p-3 overline text-xs">Type</th>
              <th className="p-3 overline text-xs">Joined</th>
              <th className="p-3 overline text-xs">Status</th>
              <th className="p-3 overline text-xs text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => {
              const self = u.user_id === me?.user_id;
              return (
                <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-border">
                  <td className="p-3">
                    <div className="font-display">{u.name}{u.company_name ? ` · ${u.company_name}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </td>
                  <td className="p-3 overline text-xs">{u.user_type?.replace("_", " ")}</td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString("en-GB")}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {u.is_admin && <Badge className="rounded-none bg-forest text-white overline">admin</Badge>}
                      {u.is_suspended && <Badge className="rounded-none bg-destructive text-white overline">suspended</Badge>}
                      {!u.is_admin && !u.is_suspended && <Badge variant="outline" className="rounded-none border-border overline">active</Badge>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {!u.is_suspended ? (
                        <Button disabled={self} size="sm" variant="outline" onClick={() => action(u, "suspend", "Suspended")} data-testid={`admin-user-suspend-${u.user_id}`} className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                          <Ban className="w-4 h-4 mr-1" /> Suspend
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => action(u, "unsuspend", "Unsuspended")} data-testid={`admin-user-unsuspend-${u.user_id}`} className="rounded-none">
                          <RotateCcw className="w-4 h-4 mr-1" /> Restore
                        </Button>
                      )}
                      {!u.is_admin ? (
                        <Button disabled={self} size="sm" variant="outline" onClick={() => action(u, "promote", "Promoted to admin")} data-testid={`admin-user-promote-${u.user_id}`} className="rounded-none">
                          <Shield className="w-4 h-4 mr-1" /> Promote
                        </Button>
                      ) : (
                        <Button disabled={self} size="sm" variant="outline" onClick={() => action(u, "demote", "Demoted")} data-testid={`admin-user-demote-${u.user_id}`} className="rounded-none">
                          <ShieldOff className="w-4 h-4 mr-1" /> Demote
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
